import { isApp } from "@/app/bridge";

/**
 * 修改 URL 中的指定参数值
 * @param url 原始 URL 字符串
 * @param key 要修改的参数名
 * @param value 新的参数值（支持字符串、数字、布尔值）
 * @returns 修改后的完整 URL 字符串
 */
export function updateUrlParameter(
  url: string,
  key: string,
  value: string | number | boolean,
): string {
  // 解析原始 URL
  const urlObj = new URL(url);

  // 创建 URLSearchParams 对象处理查询参数
  const params = new URLSearchParams(urlObj.search);

  // 若参数存在则更新值（需转换为字符串）
  if (params.has(key)) {
    params.set(key, String(value));

    // 更新 URL 的查询参数部分
    urlObj.search = params.toString();

    // 返回完整新 URL（包含可能的 hash 部分）
    return urlObj.toString();
  }

  // 参数不存在时直接返回原 URL
  return url;
}

export function removeQueryParam(
  paramName: string | string[],
  customUrl?: string,
): void {
  // 获取当前URL或使用自定义URL
  const currentUrl = customUrl || window.location.href;

  // 创建URL对象
  const urlObj = new URL(currentUrl);

  // 删除指定的查询参数
  if (typeof paramName === "string") {
    urlObj.searchParams.delete(paramName);
  } else if (Array.isArray(paramName)) {
    paramName.forEach((name) => {
      urlObj.searchParams.delete(name);
    });
  }

  // 使用history.replaceState更新URL而不刷新页面
  window.history.replaceState({}, document.title, urlObj.toString());
}

/**
 * 判断 href 是否包含 im 相关路径（这些路径不需要改写为 webview 协议）
 */
export function shouldUseWebview(href: string): boolean {
  return (
    href.includes("/cn/im") ||
    href.includes("/en/im") ||
    href.includes("/im") ||
    href.includes("/im/") ||
    href.includes("/liveChat/")
  );
}

/**
 * 将文本中的裸 URL 转换为可点击的 <a> 标签，兼容富文本（不破坏已有 HTML 标签和链接）
 * App 端会将 href 改写为 bambulab webview 协议
 * @param text 可能包含 HTML 的文本
 * @returns 处理后的文本
 */
export function validIsALink(text: string) {
  if (!text) return text;

  const urlRegex =
    /(https?:\/\/[^\s\u4E00-\u9FFF\u3000-\u303F\uFF00-\uFFEF]+)/gi;
  const htmlTagRegex = /(<[^>]+>)/g;
  const parts = text.split(htmlTagRegex);
  const appMode = isApp();

  let insideAnchor = false;

  return parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) {
        if (/^<a[\s>]/i.test(part)) {
          insideAnchor = true;
        } else if (/^<\/a\s*>/i.test(part)) {
          insideAnchor = false;
        }
        return part;
      }
      if (insideAnchor) return part;
      return part.replace(urlRegex, (_match, url) => {
        const href = url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
    })
    .join("");
}

export function getUrlParams(): URLSearchParams {
  // 确保在客户端环境执行
  if (typeof window === "undefined") {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

export function getUrlParam(name: string): string | null {
  const params = getUrlParams();
  return params.get(name);
}

export function getAllUrlParams(): Record<string, string> {
  const params = getUrlParams();
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

export const isValidUrl = (url: string): boolean => {
  try {
    if (!url) return false;
    const parsedUrl = new URL(url);
    // Only allow http and https protocols
    return ["http:", "https:"].includes(parsedUrl.protocol.toLowerCase());
  } catch (e) {
    // If URL parsing fails, it's likely not a valid URL
    return false;
  }
};
